import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BUTTON_TYPE, ButtonComponent } from '../../components/ButtonComponent';
import { useMerchants } from '../../store/merchnts/merchants-context';
import {
	fetchPartnersList,
	sentPartnerInvite,
	setPartnerFilters,
	setPartnerListDefault,
} from '../../store/merchnts/actions/partners';
import { TablesNames } from '../../store/tables/tables-context';
import { Table } from '../../components/table/Table';
import { IPreparedPartnerData } from '../../store/tables/partners/partners-data';
import { PageTitle } from '../../layout/core';
import { useNavigate } from 'react-router-dom';
import { PARTNER_NAVIGATION } from '../../constants/new-partner/partner-navigation';
import { IPartnerFilters, PartnerListFilters } from '../../components/partners/PartnerListFilters';
import { parseSearchParams } from '../../utils/utilities';
import { useLocation } from 'react-router';

export const PartnersList = (): JSX.Element => {
	const { t } = useTranslation();
	const { search } = useLocation();
	const navigate = useNavigate();

	const partnerFilters = parseSearchParams(search);
	const { merchantState: { partnersList, isPartnerListLoading }, dispatch } = useMerchants();

	const [searchValue, setSearchValue] = useState<string>(partnerFilters?.query ?? '');
	const [loading, setLoading] = useState({
		isLoading: false,
		id: ''
	});

	useEffect(() => {
		return () => {
			setPartnerListDefault(dispatch);
		};
	}, []);

	const preparedTableData: IPreparedPartnerData[] = partnersList.map((el): IPreparedPartnerData => ({
		id: el?.id,
		type: el?.type,
		partnerName: el?.name,
		contactName: el?.mainContact?.firstName && el?.mainContact?.lastName ? `${el?.mainContact?.firstName} ${el?.mainContact?.lastName}` : '-',
		email: el?.mainContact?.email ?? '-',
		phone: el?.mainContact?.phone ?? '-',
		userStatus: el?.mainContact?.userCreated,
		partnerStatus: el?.statusName,
		data: el
	}));

	const filteredList: IPreparedPartnerData[] = preparedTableData
		?.filter((partner: IPreparedPartnerData) => {
			const { id, partnerName, contactName, phone, email } = partner;
			if (contactName !== '-') {
				const fullName = contactName?.toLowerCase();
				return fullName?.includes(searchValue?.trim()?.toLowerCase())
				|| phone?.includes(searchValue?.trim()?.toLowerCase())
				|| email?.includes(searchValue?.trim()?.toLowerCase())
				|| id?.includes(searchValue?.trim()?.toLowerCase())
				|| partnerName?.toLowerCase()?.includes(searchValue?.trim()?.toLowerCase());
			}
		});

	const handleClick = async (id): Promise<void> => {
		setLoading({
			isLoading: true,
			id
		});
		await sentPartnerInvite(id);
		// await fetchPartnersList(dispatch);
		setLoading({
			isLoading: true,
			id: ''
		});
	};

	const rowActions = (rowData): JSX.Element => {
		return (
			<div className="d-flex align-items-center justify-content-center">
				<div
					className="text-muted cursor-pointer text-center fw-bolder fs-7"
					onClick={(): void => navigate(
						`/partners/${rowData?.original?.id}/${PARTNER_NAVIGATION.PARTNER_INFO}`, {
							state: { search },
						})}
				>
					View
				</div>
				{
					!rowData?.original?.userStatus
						? (
							<ButtonComponent
								buttonName={t('PARTNERS.INVITE_BTN_NAME')}
								buttonType={BUTTON_TYPE.WHITE}
								onButtonClick={async (): Promise<void> => await handleClick(rowData?.original?.id)}
								loading={loading.isLoading && loading.id === rowData?.original?.id}
								additionalClass="no-letter-spacing fs-7 py-1 ps-2 pe-3 d-block w-50px ms-5"
							/>
						) : <div className="w-50px ms-5"/>
				}
			</div>
		);
	};

	const listener = (search: IPartnerFilters): void => {
		if (Object.keys(search).length) {
			setPartnerFilters({ dispatch, filters: search });
		}

		void fetchPartnersList({ dispatch, parsedSearch: search });
	};

	return (
		<>
			<PageTitle breadcrumbs={[]}>{t('MENU.PARTNERS')}</PageTitle>

			<PartnerListFilters searchValue={searchValue} setSearchValue={setSearchValue} />

			<Table
				name={TablesNames.partnerTableData}
				data={searchValue ? filteredList : preparedTableData}
				isLoading={isPartnerListLoading}
				enableRowActions={true}
				rowActions={rowActions}
				actionColumnsSize={100}
				queryParamsListener={listener}
			/>
		</>
	);
};
